import { useFormContext, useWatch } from 'react-hook-form';

import { cx } from '@libs/classnames';
import { Form } from '@ui/components/Form';

import { ChessRussianClubTickets } from '@features/chessrussian/customizations/tournament-settings/inputs/ChessRussianClubTickets';
import { isChessRussianClub } from '@features/chessrussian/customizations/utils/isChessRussianClub';
import { BlindGameInput } from '@features/tournament/ui/components/TournamentSettings/inputs/BlindGameInput';
import { ContactsInput } from '@features/tournament/ui/components/TournamentSettings/inputs/ContactsInput';
import { EntranceInput } from '@features/tournament/ui/components/TournamentSettings/inputs/EntranceInput';
import { EventSectionInput } from '@features/tournament/ui/components/TournamentSettings/inputs/EventSectionInput';
import { GameSystemInput } from '@features/tournament/ui/components/TournamentSettings/inputs/GameSystemInput';
import { InitialFenInput } from '@features/tournament/ui/components/TournamentSettings/inputs/InitialFenInput';
import { LogoFileInput } from '@features/tournament/ui/components/TournamentSettings/inputs/LogoFileInput';
import { MatchTypeInput } from '@features/tournament/ui/components/TournamentSettings/inputs/MatchTypeInput';
import { ModeInput } from '@features/tournament/ui/components/TournamentSettings/inputs/ModeInput';
import { OrgSettingsInput } from '@features/tournament/ui/components/TournamentSettings/inputs/OrgSettingsInput';
import { RcfRankInput } from '@features/tournament/ui/components/TournamentSettings/inputs/RcfRankInput';
import { RulesFileInput } from '@features/tournament/ui/components/TournamentSettings/inputs/RulesFileInput';
import { StartTimeInput } from '@features/tournament/ui/components/TournamentSettings/inputs/StartTimeInput';
import { TitleInput } from '@features/tournament/ui/components/TournamentSettings/inputs/TitleInput';
import { TournamentSponsorsInput } from '@features/tournament/ui/components/TournamentSettings/inputs/TournamentSponsorsInput';
import { KnockoutSettings } from '@features/tournament/ui/components/TournamentSettings/KnockoutSettings';
import { OneOnOneSettings } from '@features/tournament/ui/components/TournamentSettings/OneOnOneSettings';
import { RoundRobinSettings } from '@features/tournament/ui/components/TournamentSettings/RoundRobinSettings';
import { SeanceSettings } from '@features/tournament/ui/components/TournamentSettings/SeanceSettings';
import { SwissSettings } from '@features/tournament/ui/components/TournamentSettings/SwissSettings';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';

import { OnlineStatusInput } from './inputs/OnlineStatusInput';
import { RegistrationCutoffInput } from './inputs/RegistrationCutoffInput';
import { RemoveAfterNoShowsInput } from './inputs/RemoveAfterNoShowsInput';
import { TimeBetweenRoundsInput } from './inputs/TimeBetweenRoundsInput';

import styles from './TournamentSettings.module.css';

type Props = {
  clubId: string;
  className?: string;
  onUploadLogo: (file: FormData) => Promise<void>;
  onUploadRules: (file: FormData) => Promise<void>;
  onUploadSponsorLogo: (sponsorNumber: number, file: FormData) => Promise<void>;
  isAdmin: boolean;
};

export function TournamentSettings({
  clubId,
  className,
  onUploadLogo,
  onUploadRules,
  onUploadSponsorLogo,
  isAdmin,
}: Props) {
  const { control } = useFormContext<TournamentEditFormValues>();
  const system = useWatch({
    name: 'system',
    control,
  });
  const isRcf = useWatch({
    name: 'rcfRankCalculated',
    control,
  });
  const isMatch = system === 'OneOnOne';
  const isSeance = system === 'Seance';
  const isTournament = !isMatch && !isSeance;
  const isKnockout = system === 'Knockout';
  const isRoundRobin = system === 'RoundRobin';
  const isSwiss = system === 'Swiss';

  const isChessRussian = isChessRussianClub(clubId);

  return (
    <Form className={cx(className, styles.root)}>
      <TitleInput />
      <StartTimeInput />
      {!isSeance && <TimeBetweenRoundsInput />}
      {(isTournament || isSeance) && <EntranceInput />}
      <RegistrationCutoffInput />
      <OnlineStatusInput />
      {!isTournament && <MatchTypeInput />}
      {isChessRussian ? <ChessRussianClubTickets /> : <EventSectionInput clubId={clubId} />}
      {!isSeance && isAdmin && !isChessRussian && <RcfRankInput />}
      {!isSeance && isAdmin && isRcf && <OrgSettingsInput />}
      {isTournament && <GameSystemInput />}
      {isTournament && !isRoundRobin && !isKnockout && <RemoveAfterNoShowsInput />}
      {!isSeance && <ModeInput />}
      {isSeance && <SeanceSettings />}
      {isMatch && <OneOnOneSettings />}
      {isRoundRobin && <RoundRobinSettings />}
      {isKnockout && <KnockoutSettings />}
      {isSwiss && <SwissSettings />}
      <BlindGameInput />
      <InitialFenInput />
      <RulesFileInput onUpload={onUploadRules} />
      <LogoFileInput onUpload={onUploadLogo} />
      <ContactsInput />

      <TournamentSponsorsInput onUploadSponsorLogo={onUploadSponsorLogo} />
    </Form>
  );
}
